/*
  g2m-header-search.js — mobile header search behaviour.

  Two jobs, both scoped to the header search field:

  1. Placeholder per width band. The desktop placeholder is a long sentence that
     truncates mid-word in a phone-width field, so the section renders three
     strings as data attributes and this swaps `placeholder` between them.

  2. Make tapping the field always open USF's instant-search popup on mobile.

     Background, because this is not obvious: the mobile header used to carry a
     magnifier with `data-action="toggle-search"`. Two different things were
     bound to it — Warehouse's own handler, which slides open a full-screen
     `is-fixed` search drawer, and USF's handler in assets/usf-boot.js, which
     opens its instant-search popup and calls stopEvent to suppress the drawer.
     USF also registers an `is_show` listener that clicks
     `.search-bar__close-button` 300ms *after* its popup appears, to shut the
     drawer if it opened anyway.

     That 300ms is exactly the bug the brief describes: whichever UI won the
     race is what the shopper saw, so the same tap produced the USF popup
     sometimes and the bare Warehouse drawer other times. Now that the field is
     a permanent row in the mobile header the magnifier is gone, which also
     removes USF's only mobile trigger, so we open the popup from the field and
     keep the drawer shut outright rather than closing it after the fact.

  Degrades safely: with USF absent or still loading, the field stays an ordinary
  search form that submits to /search, so search is never a dead end.
*/
(function () {
  'use strict';

  if (window.__g2mHeaderSearchInit) return;
  window.__g2mHeaderSearchInit = true;

  // .98 rather than a whole pixel on the upper bounds. A viewport is not always
  // an integer — browser zoom and some Android devices report fractional CSS
  // pixels — and at 767.33px `(max-width: 767px)` and `(min-width: 768px)` both
  // miss, so the field silently fell back to the long desktop string. These
  // bands are closed; the matching CSS in g2m-header-footer.css still breaks at
  // a whole 767px, which only differs on a fractional viewport.
  var MOBILE = '(max-width: 767.98px)';
  var TABLET = '(min-width: 768px) and (max-width: 1023.98px)';
  var FIELD = '.search-bar__input[data-g2m-placeholder]';

  function isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  /* ── 1. Placeholder per width band ──────────────────────────────────── */

  function applyPlaceholders() {
    var fields = document.querySelectorAll(FIELD);
    var attr = isMobile()
      ? 'data-g2m-placeholder-sm'
      : window.matchMedia(TABLET).matches
        ? 'data-g2m-placeholder-md'
        : 'data-g2m-placeholder';

    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var next = el.getAttribute(attr) || el.getAttribute('data-g2m-placeholder');
      // Only touch the attribute when it actually changes — rewriting it on
      // every resize tick makes some mobile browsers drop the caret.
      if (next && el.getAttribute('placeholder') !== next) {
        el.setAttribute('placeholder', next);
      }
    }
  }

  function watch(query, handler) {
    var mql = window.matchMedia(query);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(handler); // Safari < 14
    }
  }

  // matchMedia `change` alone is not enough to stay correct. It only fires when
  // a band is *crossed*, so if the very first read happens while the viewport
  // still measures 0 — which is what an iframe reports before layout settles,
  // and what a deferred script can see on a slow first paint — the mobile
  // string latches and no later event ever corrects it. Re-reading on `load`
  // and on resize makes the first read self-correcting instead of final.
  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(applyPlaceholders, 150);
  }

  /* ── 2. Mobile: the field opens USF, never the Warehouse drawer ─────── */

  var lastOpen = 0;

  // USF appends its panel to <body> and, on mobile, styles it to cover the
  // screen. g2m-header-footer.css re-anchors it under the search bar using
  // --g2m-is-top; that value has to come from the live position of the bar,
  // because the header height changes with the announcement bar, a wrapped
  // logo, or the panel opening while the page is scrolled.
  var lastBarBottom = -1;

  function positionPanel() {
    var bar = document.querySelector('.search-bar__top');
    if (!bar) return;
    var bottom = Math.round(bar.getBoundingClientRect().bottom);
    // Clamp: if the header has scrolled off, keep the panel on screen rather
    // than letting it hang above the viewport with a negative offset.
    if (bottom < 0) bottom = 0;
    // Writing the variable invalidates style for the subtree, so only write
    // when the bar has actually moved.
    if (bottom === lastBarBottom) return;
    lastBarBottom = bottom;
    document.documentElement.style.setProperty('--g2m-is-top', (bottom + 8) + 'px');
  }

  function panelIsOpen() {
    var p = document.querySelector('.usf-popup.usf-is');
    return !!p && p.getBoundingClientRect().height > 0;
  }

  // Keep the panel glued to the bar while it is open.
  //
  // A `scroll` listener looks like the obvious way to do this and does NOT work
  // here: measured on the real page, scrolling fires zero scroll events on
  // window — the header is sticky and the announcement bar collapses, so the
  // bar's position changes while nothing reports a scroll. The panel drifted to
  // a 38px gap below the bar. Polling the bar's own geometry is indifferent to
  // *why* it moved (scroll, sticky collapse, announcement bar dismissed, an app
  // banner injecting itself, the on-screen keyboard resizing the viewport), so
  // it is the only reliable signal.
  //
  // The loop only runs while the panel is open and exits as soon as it closes,
  // and positionPanel() skips the write unless the bar actually moved.
  // An interval rather than requestAnimationFrame. rAF is the usual choice for
  // position tracking, but it is tied to rendering: it stops in a backgrounded
  // or throttled document, and it never ticked at all in the offline harness
  // this was verified in, which would have made the behaviour untestable. A
  // 100ms timer is immune to that, is imperceptible for a panel that only moves
  // when the sticky header collapses, and only runs while the panel is open.
  var trackTimer = null;
  var trackUntil = 0;
  var TRACK_MS = 100;

  function stopTracking() {
    if (trackTimer === null) return;
    window.clearInterval(trackTimer);
    trackTimer = null;
  }

  // The deadline matters. USF builds the panel asynchronously and lazy-loads
  // its main bundle on the very first open, so for several seconds after the
  // tap there is no panel in the DOM yet. An earlier version exited the moment
  // panelIsOpen() was false and therefore died on frame 1, every time, before
  // the panel existed — the symptom was the panel drifting 38px from the bar on
  // scroll because nothing was ever tracking it. So: poll through the opening
  // window, keep polling while the panel is open, and stop shortly after it
  // closes.
  function startTracking() {
    trackUntil = Date.now() + 15000;
    if (trackTimer !== null) return;
    trackTimer = window.setInterval(function () {
      if (!isMobile()) {
        stopTracking();
        return;
      }
      if (panelIsOpen()) {
        trackUntil = Date.now() + 2000;
      } else if (Date.now() > trackUntil) {
        stopTracking();
        return;
      }
      positionPanel();
    }, TRACK_MS);
  }

  function trackPanel() {
    if (isMobile() && panelIsOpen()) positionPanel();
  }

  function openInstantSearch(field) {
    var usf = window.usf;
    if (!usf || !usf.utils || typeof usf.utils.loadAndShowInstantSearch !== 'function') {
      return false; // USF not here — leave the plain form alone
    }
    // USF's own input handler may fire for the same tap; opening twice in quick
    // succession re-raises `is_show` and makes the popup flicker.
    var now = Date.now();
    if (now - lastOpen < 400) return true;
    lastOpen = now;

    // Measure before opening so the panel lands in place, and again just after
    // it renders — USF builds the panel asynchronously (it lazy-loads usf.js on
    // the very first open), so the position read before the call can be stale.
    positionPanel();
    try {
      usf.utils.loadAndShowInstantSearch(field, true);
    } catch (err) {
      return false;
    }
    window.setTimeout(positionPanel, 50);
    window.setTimeout(positionPanel, 400);
    startTracking();
    return true;
  }

  // Everything below is DELEGATED from document, and that is not a style
  // preference — it is required.
  //
  // USF's installSearchInput() does this to the search field:
  //     var o = e.cloneNode(true); a.replaceChild(o, e); e = o;
  // It clones the input and swaps the clone into the DOM so it can own the
  // events. cloneNode does not copy listeners, so anything bound directly to
  // the field is silently destroyed the moment USF boots — verified on the real
  // page: the element carried USF's handlers and none of ours. The panel still
  // opened (USF's own click handler did it), which is exactly why this was easy
  // to miss: the feature looked fine while our positioning code never ran once,
  // leaving the dropdown on the CSS fallback offset instead of measuring the
  // bar. Delegation is immune to the swap, and to section re-renders.
  function onFieldActivate(field) {
    if (!isMobile() || !field) return;
    openInstantSearch(field);
  }

  function onDocumentFocusIn(event) {
    var t = event.target;
    // focusin, not focus: focus does not bubble, so it cannot be delegated.
    if (t && t.matches && t.matches(FIELD)) onFieldActivate(t);
  }

  function onDocumentClick(event) {
    var t = event.target;
    if (!t || !t.closest) return;

    // The submit button is the one control that is meant to navigate.
    if (t.closest('.search-bar__submit')) return;

    var bar = t.closest('.search-bar__top');
    if (!bar) return;

    var form = bar.closest('form[role="search"]');
    var input = form && form.querySelector('input[name="q"]');
    if (!input) return;

    // Tapping the bar must only focus the field and show suggestions — never
    // submit, redirect, or take over the screen.
    if (document.activeElement !== input) input.focus();
    onFieldActivate(input);
  }

  // Keep the Warehouse drawer shut on mobile. Suppressing the class is more
  // reliable than trying to out-order the handler that adds it: theme.js binds
  // its own listeners and the drawer can also be opened by USF's close/refocus
  // dance, so we remove the state wherever it comes from.
  function guardDrawer() {
    var wrapper = document.querySelector('.header__search-bar-wrapper');
    if (!wrapper || typeof window.MutationObserver !== 'function') return;

    new window.MutationObserver(function () {
      if (isMobile() && wrapper.classList.contains('is-fixed')) {
        wrapper.classList.remove('is-fixed');
      }
    }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── 3. Tapping the bar only opens suggestions — it never navigates ─── */

  // The division of labour is deliberate:
  //
  //   tap the bar  -> put the caret in the field and show the suggestion list.
  //                   It must never submit, redirect, or take over the screen.
  //   tap the navy -> submit, i.e. go to the search results screen. That button
  //   button          is a plain type="submit" and is left completely alone.
  //
  // So this handler only forwards the tap to the field. Focusing it fires
  // onFieldActivate above, which opens the suggestion panel — the drawer that
  // would otherwise swallow the screen is held shut by guardDrawer().
  /* ── init ───────────────────────────────────────────────────────────── */

  function init() {
    applyPlaceholders();
    guardDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  watch(MOBILE, applyPlaceholders);
  watch(TABLET, applyPlaceholders);
  window.addEventListener('resize', onResize);
  window.addEventListener('load', applyPlaceholders);
  window.addEventListener('resize', trackPanel);

  // Delegated, so they survive USF replacing the field and the theme editor
  // re-rendering the section. Registered once, at module scope, never in init().
  document.addEventListener('focusin', onDocumentFocusIn);
  document.addEventListener('click', onDocumentClick);

  // The theme editor re-renders the header through the Section Rendering API,
  // which swaps in a fresh field. The delegated listeners above survive that;
  // this is only to re-apply the placeholder and re-arm the drawer guard on the
  // new nodes.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector(FIELD)) init();
  });
})();
